import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { CloudIcon, FingerPrintIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { PasskeyCredentialSummary } from '../../models/passkey'

interface PasskeyListItemProps {
  credential: PasskeyCredentialSummary
  busy: boolean
  onRename: () => void
  onDelete: () => void
}

export const PasskeyListItem: React.FC<PasskeyListItemProps> = ({
  credential,
  busy,
  onRename,
  onDelete,
}) => {
  const { t, i18n } = useTranslation()

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'pl-PL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })

  return (
    <li className="flex items-center gap-3 py-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100">
        {credential.backed_up ? (
          <CloudIcon className="h-5 w-5 text-gray-500" />
        ) : (
          <FingerPrintIcon className="h-5 w-5 text-gray-500" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">
          {credential.name || t('profilePage.passkeys.unnamed')}
        </p>
        <p className="text-xs text-gray-500">
          {credential.last_used_at
            ? t('profilePage.passkeys.lastUsed', { date: formatDate(credential.last_used_at) })
            : t('profilePage.passkeys.created', { date: formatDate(credential.created_at) })}
        </p>
      </div>
      <div className="flex shrink-0 gap-1">
        <button
          type="button"
          aria-label={t('profilePage.passkeys.rename')}
          disabled={busy}
          onClick={onRename}
          className="rounded-md p-2 text-gray-400 hover:bg-gray-50 hover:text-gray-600
            transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PencilIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          aria-label={t('profilePage.passkeys.delete')}
          disabled={busy}
          onClick={onDelete}
          className="rounded-md p-2 text-gray-400 hover:bg-red-50 hover:text-red-600
            transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>
    </li>
  )
}
